import type {
  FileStore,
  Submission as IDBSubmission,
} from "~~/types/index-db";
import type { Submission } from "~~/types/submission";

export const useUploadOfflineData = async () => {
  const { getSubmissions, deleteSubmission, saveSubmission } =
    useSubmissionDB();
  const { saveFiles, getFiles, deleteFiles } = useFilesDB();
  const { uploadSmallFile } = useUploadFile();
  const { post } = apiRepository();
  const toast = useToast();

  const uploadedSubmissions = ref(0);
  const { submissions, error } = await getSubmissions();
  if (error) {
    toast.add({
      title: "error",
      description: error,
      color: "error",
    });
  }
  const totalSubmissions = submissions.length;

  const submissionUploadError = ref<boolean>(false);
  const fileUploadError = ref<boolean>(false);
  const uploadStatusCode = ref<number>(200);

  async function uploadSubmissionsInParallel(batchSize = 10) {
    if (error) return;
    for (let i = 0; i < submissions.length; i += batchSize) {
      if (submissionUploadError.value || fileUploadError.value) break;
      const batch = submissions.slice(i, i + batchSize);
      await Promise.all(
        batch.map(async (submission) => {
          if (submissionUploadError.value || fileUploadError.value)
            return;
          await uploadSubmission(submission);
        }),
      );
    }
  }

  async function uploadSubmission(submission: IDBSubmission) {
    if (error) return;
    deleteSubmission(submission.id!);
    const { success, data, status } = await post<Submission>(
      `/organization/${submission.organization}/form/${submission.formId}/submit`,
      { ciphertext: submission.ciphertext },
      { requiresAuthorization: true },
    );
    if (!success || !data) {
      submissionUploadError.value = true;
      uploadStatusCode.value = status;
      saveSubmission(submission);
      return;
    }
    uploadedSubmissions.value++;
    await uploadSubmissionFiles(
      submission.files,
      data.id,
      submission.organization,
      submission.formId,
    );
  }

  async function uploadPendingFiles() {
    const pendingFiles = await getFiles();
    if (pendingFiles.length > 0) {
      for (const pFiles of pendingFiles) {
        deleteFiles(pFiles.submissionId);
        await uploadSubmissionFiles(
          pFiles.files,
          pFiles.submissionId,
          pFiles.organization,
          pFiles.formId,
        );
        if (fileUploadError.value) {
          saveFiles(pFiles);
        }
      }
    }
  }

  async function uploadSubmissionFiles(
    files: FileStore[],
    submissionId: number,
    organization: string,
    formId: number,
  ) {
    for (const file of files) {
      const _file = new File([file.bytes], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
      const { success, status } = await uploadSmallFile({
        file: _file,
        type: file.questionType,
        organization: organization,
        formId: formId,
        submissionId: submissionId,
        questionId: file.questionId,
      });

      if (!success) {
        fileUploadError.value = true;
        uploadStatusCode.value = status;
        break;
      }
    }
    if (fileUploadError.value) {
      saveFiles({
        organization,
        files: files,
        formId,
        submissionId,
        createdAt: Date.now(),
      });
    }
  }

  async function removeOldData() {
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    for (const submission of submissions) {
      if (
        submission.createdAt &&
        Date.now() - submission.createdAt > sevenDays
      ) {
        await deleteSubmission(submission.id!);
      }
    }
  }

  async function retry() {
    submissionUploadError.value = false;
    fileUploadError.value = false;
    uploadStatusCode.value = 200;
    uploadedSubmissions.value = 0;

    uploadPendingFiles();
    await uploadSubmissionsInParallel();
  }

  await removeOldData();
  uploadSubmissionsInParallel();
  uploadPendingFiles();

  return {
    totalSubmissions,
    uploadedSubmissions,

    submissionUploadError,
    fileUploadError,
    retry,
    uploadStatusCode,
  };
};
